import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Calculator, FileDown, FileText, Play, Plus, Trash2, ArrowLeft, History, Upload, FileSpreadsheet, Sparkles, BookOpen, ExternalLink, Wand2 } from "lucide-react";
import { getSchema } from "./toolSchemas";
import { REFERENCE_LIBRARY } from "./referenceLibrary";
import { toast } from "sonner";

export default function Tools() {
  const { toolId } = useParams();
  if (toolId === "history") return <ToolHistory/>;
  if (toolId) return <ToolRunner toolId={toolId}/>;
  return <ToolCatalogue/>;
}

function ToolCatalogue() {
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/tools/registry").then(({ data }) => setCatalog(data)); }, []);

  return (
    <div className="space-y-6" data-testid="tools-catalogue">
      <div>
        <div className="eyebrow">Audit & Accounting Tools</div>
        <h1 className="font-sans text-3xl">Tool Catalogue</h1>
        <p className="text-sm text-slate-600 mt-1">Industry-grade calculators with journal entries, disclosure notes, audit working papers and Excel exports.</p>
      </div>

      <Input placeholder="Search tools…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" data-testid="tool-search"/>

      {catalog.map((g) => {
        const filtered = g.tools.filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()) || t.description.toLowerCase().includes(q.toLowerCase()));
        if (!filtered.length) return null;
        return (
          <div key={g.group}>
            <h2 className="font-sans text-xl mt-6 mb-3">{g.group}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((t) => (
                <Link key={t.id + g.group} to={`/portal/tools/${t.id}`} className="block" data-testid={`tool-${t.id}`}>
                  <Card className="p-4 hover:border-slate-400 hover:shadow-sm transition-all h-full">
                    <div className="flex items-start gap-3">
                      <Calculator className="h-5 w-5 text-[#C2A671] mt-0.5"/>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-slate-900">{t.name}</div>
                        <p className="text-xs text-slate-600 mt-1">{t.description}</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ToolRunner({ toolId }) {
  const navigate = useNavigate();
  const schema = getSchema(toolId);
  const initial = {};
  schema.forEach((f) => { initial[f.key] = f.default; });
  const [inputs, setInputs] = useState(initial);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [meta, setMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aiParsing, setAiParsing] = useState(false);
  const [aiAbstract, setAiAbstract] = useState(false);
  const [lastExtracted, setLastExtracted] = useState(null);
  const withNarrative = false;
  const isLease = toolId === "ind_as_116_lease";

  const aiParseAgreement = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAiParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/tools/leases/parse-agreement", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      setLastExtracted(ex);
      // Auto-fill known schema fields
      setInputs((prev) => ({
        ...prev,
        lease_term_months: ex.lease_term_months || prev.lease_term_months,
        monthly_rent: ex.monthly_rent || prev.monthly_rent,
        escalation_pct: ex.escalation_pct ?? prev.escalation_pct,
        escalation_freq_months: ex.escalation_freq_months || prev.escalation_freq_months,
        incremental_borrowing_rate: ex.incremental_borrowing_rate || prev.incremental_borrowing_rate,
        initial_direct_costs: ex.initial_direct_costs ?? prev.initial_direct_costs,
        lease_incentives: ex.lease_incentives ?? prev.lease_incentives,
        restoration_obligation: ex.restoration_obligation ?? prev.restoration_obligation,
        payment_at_period_start: ex.payment_at_period_start || prev.payment_at_period_start,
      }));
      const assumptions = (ex.assumptions || []).join(" · ");
      toast.success(`AI parsed the agreement${assumptions ? ` (assumptions: ${assumptions})` : ""}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAiParsing(false); e.target.value = ""; }
  };
  const downloadAbstractPdf = async () => {
    if (!result) { toast.error("Run the calculation first"); return; }
    setAiAbstract(true);
    try {
      const { data } = await api.post("/tools/leases/abstract-pdf",
        { extracted: lastExtracted || inputs, result },
        { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      a.download = "lease_abstract.pdf"; a.click();
      toast.success("Lease abstract PDF downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiAbstract(false); }
  };

  useEffect(() => {
    api.get("/tools/registry").then(({ data }) => {
      for (const g of data) for (const t of g.tools) if (t.id === toolId) { setMeta({ ...t, group: g.group }); return; }
    });
  }, [toolId]);

  const setField = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const setRow = (key, i, sub, v) => setInputs((s) => {
    const arr = [...(s[key] || [])];
    arr[i] = { ...arr[i], [sub]: v };
    return { ...s, [key]: arr };
  });
  const addRow = (key, cols) => setInputs((s) => ({ ...s, [key]: [...(s[key] || []), Object.fromEntries(cols.map((c) => [c.key, c.type === "number" ? 0 : ""]))] }));
  const removeRow = (key, i) => setInputs((s) => { const arr = [...(s[key] || [])]; arr.splice(i, 1); return { ...s, [key]: arr }; });

  const buildPayload = () => {
    const out = {};
    for (const f of schema) {
      let v = inputs[f.key];
      if (f.type === "number") v = parseFloat(v) || 0;
      else if (f.type === "csv_numbers") v = String(v || "").split(",").map((x) => parseFloat(x.trim()) || 0);
      else if (f.type === "select" && (v === "true" || v === "false")) v = v === "true";
      else if (f.type === "array") {
        v = (v || []).map((row) => {
          const r = { ...row };
          for (const col of f.columns) if (col.type === "number") r[col.key] = parseFloat(r[col.key]) || 0;
          return r;
        });
      }
      out[f.key] = v;
    }
    return out;
  };

  const run = async () => {
    try {
      const payload = buildPayload();
      setRunning(true);
      const { data } = await api.post("/tools/run", { tool_id: toolId, inputs: payload, label: meta?.name, with_narrative: withNarrative });
      setResult(data.result);
      toast.success("Calculation complete");
    } catch (e) { if (e.message !== "bad json") toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const download = async (format) => {
    try {
      const payload = buildPayload();
      const url = `/tools/${toolId}/export-${format}`;
      const { data } = await api.post(url, { tool_id: toolId, inputs: payload, with_narrative: withNarrative }, { responseType: "blob" });
      const mime = format === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const blob = new Blob([data], { type: mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${toolId}.${format}`;
      a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/tools/${toolId}/template`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      a.download = `${toolId}_template.xlsx`;
      a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadInputs = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tools/${toolId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Map parsed values into local inputs state
      const next = { ...inputs };
      Object.entries(data.inputs || {}).forEach(([k, v]) => {
        if (Array.isArray(v)) next[k] = v.join(",");
        else if (typeof v === "boolean") next[k] = String(v);
        else next[k] = v;
      });
      setInputs(next);
      toast.success("Inputs loaded from Excel");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-6" data-testid={`tool-runner-${toolId}`}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/portal/tools")}><ArrowLeft className="h-4 w-4 mr-1"/>Back</Button>
        <div>
          <div className="eyebrow">{meta?.group || "Audit & Accounting Tools"}</div>
          <h1 className="font-sans text-2xl">{meta?.name || toolId}</h1>
          {meta?.description && <p className="text-sm text-slate-600">{meta.description}</p>}
        </div>
      </div>

      <Card className="p-5">
        <div className="text-sm font-semibold mb-3">Inputs</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {schema.map((f) => (
            <div key={f.key} className={f.type === "array" || f.type === "json" || f.type === "csv_numbers" ? "md:col-span-2" : ""}>
              <label className="text-xs text-slate-500">{f.label}</label>
              {f.type === "number" && <Input type="number" step="any" value={inputs[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`input-${f.key}`}/>}
              {f.type === "text" && <Input value={inputs[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`input-${f.key}`}/>}
              {f.type === "csv_numbers" && <Input value={inputs[f.key] ?? ""} onChange={(e) => setField(f.key, e.target.value)} placeholder="1000, 1100, 1200" data-testid={`input-${f.key}`}/>}
              {f.type === "select" && (
                <Select value={String(inputs[f.key])} onValueChange={(v) => setField(f.key, v)}>
                  <SelectTrigger data-testid={`input-${f.key}`}><SelectValue/></SelectTrigger>
                  <SelectContent>{f.options.map(([v, l]) => <SelectItem key={String(v)} value={String(v)}>{l}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.type === "array" && (
                <div className="border border-slate-200 rounded p-2">
                  <table className="w-full text-xs">
                    <thead><tr>{f.columns.map((c) => <th key={c.key} className="text-left p-1 text-slate-500">{c.label}</th>)}<th></th></tr></thead>
                    <tbody>
                      {(inputs[f.key] || []).map((row, i) => (
                        <tr key={i}>
                          {f.columns.map((c) => (
                            <td key={c.key} className="p-1">
                              <Input type={c.type === "number" ? "number" : "text"} step="any" value={row[c.key] ?? ""} onChange={(e) => setRow(f.key, i, c.key, e.target.value)} className="h-8 text-xs"/>
                            </td>
                          ))}
                          <td><Button size="sm" variant="ghost" onClick={() => removeRow(f.key, i)}><Trash2 className="h-3 w-3"/></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Button size="sm" variant="outline" onClick={() => addRow(f.key, f.columns)} className="mt-2"><Plus className="h-3 w-3 mr-1"/>Add Row</Button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <Button onClick={run} disabled={running} data-testid="run-tool"><Play className="h-4 w-4 mr-1"/>{running ? "Running…" : "Run Calculation"}</Button>
          <div className="flex-1"/>
          {isLease && (
            <label className={`inline-flex items-center gap-1 px-3 py-2 text-sm border border-violet-300 bg-violet-50 text-violet-800 rounded cursor-pointer hover:bg-violet-100 ${aiParsing ? "opacity-50 cursor-wait" : ""}`} data-testid="ai-parse-btn" title="Upload lease agreement PDF/DOCX — AI extracts inputs">
              <Wand2 className={`h-4 w-4 ${aiParsing ? "animate-pulse" : ""}`}/>{aiParsing ? "AI reading…" : "AI: Read Agreement"}
              <input type="file" accept="application/pdf,.pdf,.docx,.doc,.txt" className="hidden" onChange={aiParseAgreement} disabled={aiParsing}/>
            </label>
          )}
          {isLease && result && (
            <Button variant="outline" onClick={downloadAbstractPdf} disabled={aiAbstract} data-testid="ai-abstract-btn">
              <FileText className="h-4 w-4 mr-1"/>{aiAbstract ? "Preparing…" : "AI Abstract PDF"}
            </Button>
          )}
          <Button variant="outline" onClick={downloadTemplate} data-testid="dl-template"><FileSpreadsheet className="h-4 w-4 mr-1"/>Template</Button>
          <label className={`inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 rounded cursor-pointer hover:bg-slate-50 ${uploading ? "opacity-50" : ""}`} data-testid="upload-excel-btn">
            <Upload className="h-4 w-4"/>{uploading ? "Uploading…" : "Upload Excel"}
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={uploadInputs} disabled={uploading}/>
          </label>
          <Button variant="outline" onClick={() => download("xlsx")} data-testid="export-xlsx"><FileDown className="h-4 w-4 mr-1"/>Excel</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="export-pdf"><FileText className="h-4 w-4 mr-1"/>PDF</Button>
        </div>
      </Card>

      <ToolSectionRefs toolId={toolId}/>

      {result && <ToolResult result={result}/>}
    </div>
  );
}

function ToolSectionRefs({ toolId }) {
  // Map tool to a KIP section for relevant references.
  const mapping = {
    ind_as_116_lease: "audit", lease_modification: "audit", ecl_simplified: "audit", ecl_pd_lgd_ead: "audit",
    ecl_provision_matrix: "audit", impairment_ind_as_36: "audit", revenue_recognition_115: "audit",
    percentage_completion: "audit", ppa_ind_as_103: "audit", goodwill_calculator: "audit", nci_calculator: "audit",
    contingent_consideration: "audit", consolidation: "audit", fx_translation: "audit", deferred_tax: "audit",
    eir_calculator: "audit", security_deposit_fv: "audit", financial_guarantee: "audit",
    materiality: "audit", sampling: "audit", benford_law: "audit", duplicate_payment_detector: "audit",
    variance_analysis: "audit", analytical_review: "audit", lead_schedule_generator: "audit",
    journal_entry_testing: "audit", caro_applicability: "audit", audit_query_tracker: "audit",
    mrl_generator: "audit", rcm_generator: "gst",
    dcf: "valuation", wacc: "valuation", fcff: "valuation", fcfe: "valuation", terminal_value: "valuation",
    esop_black_scholes: "valuation", esop_binomial: "valuation", rule_11ua: "valuation",
    customer_relationship_mpeem: "valuation", brand_relief_from_royalty: "valuation", technology_royalty: "valuation",
    company_law_applicability: "mca", csr_applicability: "mca",
    tax_provision: "income_tax", etr_reconciliation: "income_tax", mat_credit: "income_tax",
    fdi_check: "rbi_fema", ecb_calculator: "rbi_fema", odi_check: "rbi_fema", fc_gpr: "rbi_fema", fc_trs: "rbi_fema",
    loan_modification: "audit", related_party_loan_valuation: "audit", lease_vs_buy: "audit",
  };
  const section = mapping[toolId] || "audit";
  const items = REFERENCE_LIBRARY.filter((r) => r.section === section).slice(0, 6);
  if (!items.length) return null;
  return (
    <Card className="p-4 bg-amber-50/40 border-amber-200" data-testid="tool-section-refs">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="h-4 w-4 text-[#C2A671]"/>
        <h3 className="font-semibold text-slate-900 text-sm">Relevant Acts, Rules &amp; Regulations</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2">
        {items.map((r) => (
          <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" className="border border-amber-200 rounded p-2 text-sm bg-white hover:border-amber-400" data-testid={`tool-ref-${r.id}`}>
            <div className="font-medium text-slate-900 text-xs line-clamp-2 leading-tight">{r.label}</div>
            <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">{r.source} <ExternalLink className="h-3 w-3"/></div>
          </a>
        ))}
      </div>
    </Card>
  );
}

const fmtVal = (k, v) => {
  if (typeof v === "number") return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (typeof v === "boolean") return k.includes("applicable") ? (v ? "Applicable" : "Not applicable") : (v ? "Yes" : "No");
  if (Array.isArray(v)) return v.length ? v.join("; ") : "—";
  return String(v);
};

export function ToolResult({ result }) {
  const summary = result.summary || {};
  const schedule = result.schedule || [];
  const journalEntries = result.journal_entries || [];
  const methodology = result.methodology || [];
  return (
    <Card className="p-5" data-testid="tool-result">
      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          {methodology.length > 0 && <TabsTrigger value="methodology" data-testid="tab-methodology">Methodology</TabsTrigger>}
          {schedule.length > 0 && <TabsTrigger value="schedule">Schedule</TabsTrigger>}
          {journalEntries.length > 0 && <TabsTrigger value="je">Journal Entries</TabsTrigger>}
          {result.disclosure && <TabsTrigger value="disclosure">Disclosure</TabsTrigger>}
          {result.audit_working_paper && <TabsTrigger value="wp">Working Paper</TabsTrigger>}
        </TabsList>

        <TabsContent value="summary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {Object.entries(summary).map(([k, v]) => {
              const long = typeof v === "string" && v.length > 40;
              return (
              <div key={k} className={`bg-slate-50 border border-slate-200 rounded p-3 ${long ? "col-span-2 md:col-span-4" : ""}`} data-testid={`summary-${k}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                <div className={long ? "text-sm mt-1 text-slate-700 leading-relaxed" : "text-base font-mono font-semibold mt-1 text-slate-900"}>
                  {fmtVal(k, v)}
                </div>
              </div>
              );
            })}
          </div>
        </TabsContent>

        {methodology.length > 0 && (
          <TabsContent value="methodology">
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="text-left p-2 w-12">#</th>
                    <th className="text-left p-2">Step</th>
                    <th className="text-left p-2">Formula</th>
                    <th className="text-left p-2">Computed Value</th>
                  </tr>
                </thead>
                <tbody>
                  {methodology.map((m, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2 font-mono text-xs">{i + 1}</td>
                      <td className="p-2">{m.step}</td>
                      <td className="p-2 font-mono text-xs text-slate-700">{m.formula}</td>
                      <td className="p-2 font-mono text-xs">{typeof m.value === "number" ? m.value.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : String(m.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {schedule.length > 0 && (
          <TabsContent value="schedule">
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                  <tr>{Object.keys(schedule[0]).map((k) => <th key={k} className="text-left p-2">{k.replace(/_/g, " ")}</th>)}</tr>
                </thead>
                <tbody>
                  {schedule.slice(0, 200).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      {Object.entries(r).map(([k, v]) => (
                        <td key={k} className="p-2 font-mono text-xs">{typeof v === "number" ? v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : String(v)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {schedule.length > 200 && <p className="text-xs text-slate-500 mt-2">Showing first 200 of {schedule.length} rows. Download Excel for full data.</p>}
            </div>
          </TabsContent>
        )}

        {journalEntries.length > 0 && (
          <TabsContent value="je">
            <div className="space-y-4 mt-4">
              {journalEntries.map((je, i) => (
                <div key={i}>
                  <div className="text-sm font-semibold mb-1">{je.narration}</div>
                  <table className="w-full text-sm border border-slate-200">
                    <thead className="bg-slate-50 text-xs uppercase"><tr><th className="text-left p-2">Account</th><th className="text-right p-2">Debit</th><th className="text-right p-2">Credit</th></tr></thead>
                    <tbody>
                      {(je.entries || []).map((e, j) => (
                        <tr key={j} className="border-t border-slate-100">
                          <td className="p-2">{e.account}</td>
                          <td className="p-2 text-right font-mono">{e.debit ? Number(e.debit).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}</td>
                          <td className="p-2 text-right font-mono">{e.credit ? Number(e.credit).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {result.disclosure && (
          <TabsContent value="disclosure">
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded p-4 text-sm leading-relaxed">{result.disclosure}</div>
          </TabsContent>
        )}

        {result.audit_working_paper && (
          <TabsContent value="wp">
            <div className="mt-4 space-y-3 text-sm">
              {result.audit_working_paper.objective && (<div><b>Objective:</b> {result.audit_working_paper.objective}</div>)}
              {result.audit_working_paper.procedures && (
                <div><b>Audit Procedures:</b>
                  <ol className="list-decimal pl-6 mt-1 space-y-1">
                    {result.audit_working_paper.procedures.map((p, i) => <li key={i}>{p}</li>)}
                  </ol>
                </div>
              )}
              {result.audit_working_paper.key_assertions && (
                <div><b>Key Assertions:</b> {result.audit_working_paper.key_assertions.join(", ")}</div>
              )}
            </div>
          </TabsContent>
        )}

        {result.ai_narrative && (
          <TabsContent value="ai">
            <div className="mt-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-2"><Sparkles className="h-3 w-3 text-[#C2A671]"/>Generated by Claude Sonnet 4.5 — Big Four-style audit memorandum</div>
              <pre className="whitespace-pre-wrap font-sans text-sm bg-slate-50 border border-slate-200 rounded p-4 leading-relaxed">{result.ai_narrative}</pre>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </Card>
  );
}

function ToolHistory() {
  const [runs, setRuns] = useState([]);
  useEffect(() => { api.get("/tools/runs").then(({ data }) => setRuns(data)); }, []);
  return (
    <div className="space-y-4" data-testid="tools-history">
      <div>
        <div className="eyebrow">Audit & Accounting Tools</div>
        <h1 className="font-sans text-3xl">My Calculation History</h1>
      </div>
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Tool</th>
              <th className="text-left p-3">Label</th>
              <th className="text-left p-3">Key Result</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const s = r.result?.summary || {};
              const firstKey = Object.keys(s)[0];
              return (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="p-3 text-slate-500 text-xs">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3"><Link to={`/portal/tools/${r.tool_id}`} className="text-blue-600 hover:underline">{r.tool_id}</Link></td>
                  <td className="p-3">{r.label || "—"}</td>
                  <td className="p-3 font-mono text-xs">{firstKey ? `${firstKey}: ${s[firstKey]}` : "—"}</td>
                </tr>
              );
            })}
            {!runs.length && <tr><td colSpan="4" className="text-center text-slate-400 p-8">No runs yet</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
